import React, {useState, useEffect, useRef} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

/** Components */
import Skeleton from '@mui/material/Skeleton';
import PageContainer from '../../components/PageContainer';
import { PageHeader } from '../../components/ui';
import { Grid, Paper, Typography, Box, Chip, useTheme } from '@mui/material';
import { Pie, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title
} from 'chart.js';

/** Selectors */
import {MockupsCompareStats} from '../../store/slices'
import {IStore} from "../../store/types";
import MockupStatsFilters from "../../components/MockupStatsFilters";
import Dayjs from "dayjs";

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const DEFAULT_PAGING_STATE = {
    maxDate: Dayjs(new Date()).format("YYYY-MM-DD"),
    minDate: Dayjs(new Date()).format("YYYY-MM-DD"),
    template1: 'ab-current',
    template2: 'ab-test'
};

const MockupsCompareStatsPage = () => {
    const theme = useTheme();
    const {
        compareStats,
        status,
        queryParams
    } = useSelector<IStore, { compareStats: any, status: string , queryParams: any}>(state => ({
        compareStats: state.mockupsCompareStats.data,
        status: state.mockupsCompareStats.status,
        queryParams: state.mockupsCompareStats.queryParams
    }));
    const dispatch = useDispatch();
    // const classes = useStyles({columnsValue: 4});

    const loadCompareStats = (queryParams) => {
        dispatch<any>(MockupsCompareStats.getMockupsCompareStats(queryParams));
    };

    useEffect(() => {
        loadCompareStats({
            ...queryParams,
            minDate: queryParams?.minDate || DEFAULT_PAGING_STATE.minDate,
            maxDate: queryParams?.maxDate || DEFAULT_PAGING_STATE.maxDate,
            template1: queryParams?.template1 || DEFAULT_PAGING_STATE.template1,
            template2: queryParams?.template2 || DEFAULT_PAGING_STATE.template2,
        });
    }, [])

    const renderTemplateStats = (stats: any, templateName: string) => {
        if (!stats) return null;

        return (
            <Grid container spacing={2}>
                {/* General Stats */}
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>{templateName} Stats</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2">Event Count</Typography>
                                <Typography variant="h6">{stats.eventCount || '-'}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2">Customer Count</Typography>
                                <Typography variant="h6">{stats.customerCount || '-'}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2">Customer to Cart %</Typography>
                                <Typography variant="h6">{stats.customerToCartPct || '-'}%</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2">Avg Mockup Duration</Typography>
                                <Typography variant="h6">{stats.mockupDurationAvg || '-'}s</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>

                {/* End Status Categories */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>End Status </Typography>
                        {stats.endStatusCountCat && (
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Chip 
                                        label={`Success: ${stats.endStatusCountCat.success} (${stats.endStatusPctCat?.success}%)`}
                                        color="success"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Chip 
                                        label={`Error: ${stats.endStatusCountCat.error} (${stats.endStatusPctCat?.error}%)`}
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Chip 
                                        label={`Not Ended: ${stats.endStatusCountCat.notEnded} (${stats.endStatusPctCat?.notEnded}%)`}
                                        color="warning"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Grid>

                {/* Process Duration Categories */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Mockup Duration </Typography>
                        {stats.actualMockupDurationPctCat && (
                            <Grid container spacing={1}>
                                <Grid item xs={3}>
                                    <Chip 
                                        label={`Fast: ${stats.actualMockupDurationPctCat.fast}%`}
                                        color="success"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Chip 
                                        label={`Medium: ${stats.actualMockupDurationPctCat.medium}%`}
                                        color="info"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Chip 
                                        label={`Slow: ${stats.actualMockupDurationPctCat.slow}%`}
                                        color="warning"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Chip 
                                        label={`Extra Slow: ${stats.actualMockupDurationPctCat.extraSlow}%`}
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Grid>

                {/* Error Types Distribution */}
                {stats.endStatusErrorPct && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Error Types </Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Chip 
                                        label={`Mockup Error: ${stats.endStatusErrorPct.errMockup}%`}
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Chip 
                                        label={`Remove BG Error: ${stats.endStatusErrorPct.errRemovebg}%`}
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Chip 
                                        label={`Network Error: ${stats.endStatusErrorPct.errNetwork}%`}
                                        color="error"
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        );
    };

    const renderSummaryComparison = () => {
        if (!compareStats?.template1Stats || !compareStats?.template2Stats) return null;

        const template1 = compareStats.template1Stats;
        const template2 = compareStats.template2Stats;

        const metrics = [
            {
                name: 'Customer to Cart %',
                template1: parseFloat(template1.customerToCartPct || '0'),
                template2: parseFloat(template2.customerToCartPct || '0'),
                format: (val) => `${val}%`,
                higher_is_better: true
            },
            {
                name: 'Success Rate %',
                template1: parseFloat(template1.endStatusPctCat?.success || '0'),
                template2: parseFloat(template2.endStatusPctCat?.success || '0'),
                format: (val) => `${val}%`,
                higher_is_better: true
            },
            {
                name: 'Error Rate %',
                template1: parseFloat(template1.endStatusPctCat?.error || '0'),
                template2: parseFloat(template2.endStatusPctCat?.error || '0'),
                format: (val) => `${val}%`,
                higher_is_better: false
            },
            {
                name: 'Avg Mockup Duration',
                template1: parseFloat(template1.mockupDurationAvg || '0'),
                template2: parseFloat(template2.mockupDurationAvg || '0'),
                format: (val) => `${val}s`,
                higher_is_better: false
            }
        ];

        const getWinnerIcon = (metric) => {
            const t1Better = metric.higher_is_better ? 
                metric.template1 > metric.template2 : 
                metric.template1 < metric.template2;
            return t1Better ? 1 : 2;
        };

        return (
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                    Key Metrics Comparison
                </Typography>
                <Grid container spacing={2}>
                    {metrics.map((metric, index) => {
                        const winner = getWinnerIcon(metric);
                        const improvement = metric.higher_is_better ? 
                            ((metric.template2 - metric.template1) / metric.template1 * 100) :
                            ((metric.template1 - metric.template2) / metric.template1 * 100);
                        
                        return (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Paper sx={{ p: 2, bgcolor: theme.palette.action.hover, border: `1px solid ${theme.palette.divider}` }}>
                                    <Typography variant="subtitle2" gutterBottom>{metric.name}</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Box>
                                            <Typography variant="body2">Template 1: {metric.format(metric.template1)}</Typography>
                                            <Typography variant="body2">Template 2: {metric.format(metric.template2)}</Typography>
                                        </Box>
                                        {winner === 1 && <Chip label="T1 Wins" color="success" size="small" />}
                                        {winner === 2 && <Chip label="T2 Wins" color="primary" size="small" />}
                                    </Box>
                                    {Math.abs(improvement) > 0.1 && (
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: improvement > 0 ? 'green' : 'red',
                                                fontWeight: 600 
                                            }}
                                        >
                                            {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}% vs Template 1
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
        );
    };

    const renderStatisticalSignificance = () => {
        if (!compareStats?.template1Stats || !compareStats?.template2Stats) return null;

        const template1 = compareStats.template1Stats;
        const template2 = compareStats.template2Stats;

        // Simple statistical significance approximation
        const t1Conversion = parseFloat(template1.customerToCartPct || '0');
        const t2Conversion = parseFloat(template2.customerToCartPct || '0');
        const t1Sample = template1.customerCount || 0;
        const t2Sample = template2.customerCount || 0;
        
        const conversionDiff = Math.abs(t2Conversion - t1Conversion);
        const isSignificant = conversionDiff > 5 && t1Sample > 100 && t2Sample > 50; // Simple heuristic
        
        const recommendedAction = () => {
            if (!isSignificant) return "📊 Need more data to determine significance";
            if (t2Conversion > t1Conversion + 2) return "🚀 Consider implementing Template 2";
            if (t1Conversion > t2Conversion + 2) return "🛡️ Keep current Template 1";
            return "⚖️ Results are close - continue testing";
        };

        return (
            <Paper sx={{ p: 3, mb: 3, bgcolor: isSignificant ? '#f0f9ff' : '#fff8f0', border: isSignificant ? '2px solid #0ea5e9' : '2px solid #f59e0b' }}>
                <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                    🔍 Test Results Summary
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" sx={{ color: isSignificant ? '#10b981' : '#f59e0b', fontWeight: 'bold' }}>
                                {isSignificant ? '✅' : '⏳'}
                            </Typography>
                            <Typography variant="h6">
                                {isSignificant ? 'Significant' : 'Need More Data'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Conversion difference: {conversionDiff.toFixed(2)}%
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                                {t1Sample + t2Sample}
                            </Typography>
                            <Typography variant="h6">Total Samples</Typography>
                            <Typography variant="body2" color="textSecondary">
                                T1: {t1Sample} | T2: {t2Sample}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" sx={{ color: '#6366f1', fontWeight: 'bold' }}>
                                {Math.max(t1Sample, t2Sample) > 0 ? Math.floor((Math.min(t1Sample, t2Sample) / Math.max(t1Sample, t2Sample)) * 100) : 0}%
                            </Typography>
                            <Typography variant="h6">Sample Balance</Typography>
                            <Typography variant="body2" color="textSecondary">
                                Distribution quality
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(4, 125, 149, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                        🎯 Recommendation
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {recommendedAction()}
                    </Typography>
                </Box>
            </Paper>
        );
    };

    const renderComparisonCharts = () => {
        if (!compareStats?.template1Stats || !compareStats?.template2Stats) return null;

        const template1 = compareStats.template1Stats;
        const template2 = compareStats.template2Stats;

        const endStatusData = {
            labels: ['Success', 'Error', 'Not Ended'],
            datasets: [
                {
                    label: `Template 1 (${template1.eventCount} events)`,
                    data: [
                        template1.endStatusCountCat?.success || 0,
                        template1.endStatusCountCat?.error || 0,
                        template1.endStatusCountCat?.notEnded || 0
                    ],
                    backgroundColor: ['#4caf50', '#f44336', '#ff9800'],
                    borderWidth: 2,
                    borderColor: theme.palette.background.paper
                },
                {
                    label: `Template 2 (${template2.eventCount} events)`,
                    data: [
                        template2.endStatusCountCat?.success || 0,
                        template2.endStatusCountCat?.error || 0,
                        template2.endStatusCountCat?.notEnded || 0
                    ],
                    backgroundColor: ['#81c784', '#e57373', '#ffb74d'],
                    borderWidth: 2,
                    borderColor: theme.palette.background.paper
                }
            ]
        };

        const conversionData = {
            labels: ['Template 1', 'Template 2'],
            datasets: [{
                label: 'Customer to Cart %',
                data: [
                    parseFloat(template1.customerToCartPct || '0'),
                    parseFloat(template2.customerToCartPct || '0')
                ],
                backgroundColor: [theme.palette.primary.main, '#0ea5e9'],
                borderWidth: 0
            }]
        };

        const performanceComparisonData = {
            labels: ['Conversion Rate %', 'Success Rate %', 'Error Rate %'],
            datasets: [
                {
                    label: 'Template 1',
                    data: [
                        parseFloat(template1.customerToCartPct || '0'),
                        parseFloat(template1.endStatusPctCat?.success || '0'),
                        parseFloat(template1.endStatusPctCat?.error || '0')
                    ],
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1
                },
                {
                    label: 'Template 2',
                    data: [
                        parseFloat(template2.customerToCartPct || '0'),
                        parseFloat(template2.endStatusPctCat?.success || '0'),
                        parseFloat(template2.endStatusPctCat?.error || '0')
                    ],
                    backgroundColor: '#0ea5e9',
                    borderColor: '#0ea5e9',
                    borderWidth: 1
                }
            ]
        };

                return (
            <Grid container spacing={3} sx={{ mt: 2 }}>
                {/* Performance Comparison with Large Numbers */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Performance Comparison</Typography>
                        <Box sx={{ height: 280 }}>
                            <Bar 
                                data={performanceComparisonData} 
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            position: 'top',
                                            labels: {
                                                color: theme.palette.text.primary,
                                                font: {
                                                    size: 14,
                                                    weight: 'bold'
                                                }
                                            }
                                        },
                                        tooltip: {
                                            titleFont: {
                                                size: 16
                                            },
                                            bodyFont: {
                                                size: 14
                                            },
                                            callbacks: {
                                                label: function(context) {
                                                    return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
                                                }
                                            }
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            max: 100,
                                            ticks: {
                                                color: theme.palette.text.secondary,
                                                font: {
                                                    size: 14,
                                                    weight: 'bold'
                                                },
                                                callback: function(value) {
                                                    return value + '%';
                                                }
                                            }
                                        },
                                        x: {
                                            ticks: {
                                                color: theme.palette.text.secondary,
                                                font: {
                                                    size: 12,
                                                    weight: 'bold'
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        </Box>
                        {/* Performance Numbers Table */}
                        <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Conversion:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        T1: {parseFloat(template1.customerToCartPct || '0').toFixed(1)}%
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        T2: {parseFloat(template2.customerToCartPct || '0').toFixed(1)}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Success:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        T1: {parseFloat(template1.endStatusPctCat?.success || '0').toFixed(1)}%
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        T2: {parseFloat(template2.endStatusPctCat?.success || '0').toFixed(1)}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Error:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        T1: {parseFloat(template1.endStatusPctCat?.error || '0').toFixed(1)}%
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        T2: {parseFloat(template2.endStatusPctCat?.error || '0').toFixed(1)}%
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>

                {/* Sample Distribution with Large Numbers */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>Events</Typography>
                        <Box sx={{ height: 280, display: 'flex', justifyContent: 'center' }}>
                            <Pie 
                                data={{
                                    labels: ['Template 1', 'Template 2'],
                    datasets: [{
                        data: [template1.eventCount || 0, template2.eventCount || 0],
                        backgroundColor: [theme.palette.primary.main, '#0ea5e9'],
                        borderWidth: 3,
                        borderColor: theme.palette.background.paper
                    }]
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            position: 'bottom',
                                            labels: {
                                                color: theme.palette.text.primary,
                                                padding: 15,
                                                font: {
                                                    size: 14,
                                                    weight: 'bold'
                                                }
                                            }
                                        },
                                        tooltip: {
                                            titleFont: {
                                                size: 16
                                            },
                                            bodyFont: {
                                                size: 14
                                            },
                                            callbacks: {
                                                label: function(context) {
                                                    const total = context.dataset.data.reduce((a, b) => Number(a) + Number(b), 0);
                                                    const percentage = ((Number(context.parsed) / total) * 100).toFixed(1);
                                                    return `${context.label}: ${context.parsed} events (${percentage}%)`;
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        </Box>
                        {/* Event Count Numbers Table */}
                        <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1, textAlign: 'center' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Template 1
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {template1.eventCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        events
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0ea5e9' }}>
                                        Template 2
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0ea5e9' }}>
                                        {template2.eventCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        events
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    Total: {(template1.eventCount || 0) + (template2.eventCount || 0)} events
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    return (
        <PageContainer constrained>
            <div style={{ width: '100%' }}>
            <PageHeader
                eyebrow="Mockups"
                title="A/B testing"
                subtitle="Compare the two mockup templates on events, customers, conversion and generation time."
            />
            
            <MockupStatsFilters 
                queryParams={{...DEFAULT_PAGING_STATE, ...queryParams}} 
                loadMockups={loadCompareStats} 
                loading={status === 'loading'} 
                columCount={() => {}}
                isCompareMode={true}
            />
            
            {(compareStats && Object.keys(compareStats).length !== 0 && status !== 'loading') &&
                <Box sx={{ mt: 4 }}>
                    {/* Statistical Significance */}
                    {/*{renderStatisticalSignificance()}*/}
                    
                    {/* Summary Comparison */}
                    {renderSummaryComparison()}
                    
                    {/*/!* Comparison Charts *!/*/}
                    {/*{renderComparisonCharts()}*/}
                    
                    {/* Detailed Template Stats */}
                    <Typography variant="h5" sx={{ mt: 6, mb: 3, color: theme.palette.primary.main, fontWeight: 600 }}>
                        Detailed Analytics
                    </Typography>
                    
                    {/* Template 1 Stats */}
                    {renderTemplateStats(compareStats.template1Stats, 'Template 1 (Current)')}
                    
                    {/* Template 2 Stats */}
                    <Box sx={{ mt: 4 }}>
                        {renderTemplateStats(compareStats.template2Stats, 'Template 2 (Test)')}
                    </Box>

                    {/* Comparison Charts */}
                    {renderComparisonCharts()}
                </Box>
            }
            
            {status === 'loading' &&
                <Skeleton animation="wave" width={'100%'} height={600} variant="rounded" />
            }
            </div>
        </PageContainer>
    );
};

export default MockupsCompareStatsPage; 